import { useContext, useEffect, useMemo, useState } from "react";
import useAsyncEffect from "use-async-effect";
import { AppContext } from "../State/Context";
import { ActionType } from "../State/State";
import { RequestMethod, askAPI } from "../api/api";
import { Hexagen, Player } from "../types";
import { CardTileList } from "../Card/CardTileList";

export const PlayerInfoPopup: React.FC = () => {
  const { dispatch, state } = useContext(AppContext);
  const [playerInfo, setPlayerInfo] = useState<Player | null>(null);
  const [hexagonsToShow, setHexagonsToShow] = useState<Hexagen[]>([]);
  const [selectedMethod, setSelectedMethod] = useState<
    "newest" | "oldest" | "points" | "upvotes"
  >("newest");
  const [sortingMethod, setSortingMethod] = useState<"asc" | "desc">("asc");
  const [currentPage, setCurrentPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const totalHexagons = playerInfo?.hexagensNumber || 0;
  const totalPages = Math.ceil(totalHexagons / 24);

  const zIndex = useMemo(() => {
    return Math.floor(Date.now() / 1000);
  }, []);

  const getVisiblePages = () => {
    const pages: (number | string)[] = [];

    if (totalPages <= 7) {
      for (let i = 0; i < totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage < 3) {
        for (let i = 0; i < 5; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages - 1);
      } else if (currentPage > totalPages - 4) {
        pages.push(0);
        pages.push("...");
        for (let i = totalPages - 5; i < totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(0);
        pages.push("...");
        pages.push(currentPage - 3);
        pages.push(currentPage - 2);
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push(currentPage + 2);
        pages.push(currentPage + 3);
        pages.push("...");
        pages.push(totalPages - 1);
      }
    }

    return pages;
  };

  const goToPage = (page: number) => {
    if (page < 0 || page >= totalPages || page === currentPage || isLoading)
      return;

    const ascending = sortingMethod === "asc";
    if (selectedMethod === "points") {
      fetchHexagensByPoints(ascending, page);
    } else if (selectedMethod === "upvotes") {
      fetchHexagensByUpvotes(ascending, page);
    } else {
      fetchHexagensByDate(ascending, page);
    }
  };

  const closePopup = () => {
    dispatch({ type: ActionType.TOGGLE_PLAYER_INFO_POPUP });
    window.history.pushState(null, "", "/");
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      closePopup();
    };
    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [dispatch]);

  const fetchPlayerInfo = async () => {
    const uid = state.selectedPlayerUID;
    if (!uid) return;

    try {
      const url = `/player/${uid}`;
      window.history.pushState(null, "", url);

      const playerData = await askAPI<Player>(
        `/api/player/info/${uid}/`,
        RequestMethod.GET
      );

      if (!playerData) {
        return;
      }

      setPlayerInfo(playerData);
    } catch (error) {
      console.error("Failed to fetch player info:", error);
    }
  };

  const fetchHexagensByDate = async (ascending: boolean, page: number = 0) => {
    const uid = state.selectedPlayerUID;
    if (!uid) return;

    setIsLoading(true);
    setSelectedMethod(ascending ? "oldest" : "newest");
    setSortingMethod(ascending ? "asc" : "desc");

    try {
      const url = `/api/hexagen/by-date-by-player/${uid}/${
        ascending ? "asc" : "desc"
      }/${page}`;
      const hexagons = await askAPI<Hexagen[]>(url, RequestMethod.GET);
      if (hexagons) {
        setHexagonsToShow(hexagons);
        setCurrentPage(page);
      }
    } catch (error) {
      console.error("Failed to fetch hexagons:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchHexagensByPoints = async (
    ascending: boolean,
    page: number = 0
  ) => {
    const uid = state.selectedPlayerUID;
    if (!uid) return;

    setIsLoading(true);
    setSelectedMethod("points");
    setSortingMethod(ascending ? "asc" : "desc");

    try {
      const url = `/api/hexagen/by-points-by-player/${uid}/${
        ascending ? "asc" : "desc"
      }/${page}`;
      const hexagons = await askAPI<Hexagen[]>(url, RequestMethod.GET);
      if (hexagons) {
        setHexagonsToShow(hexagons);
        setCurrentPage(page);
      }
    } catch (error) {
      console.error("Failed to fetch hexagons:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchHexagensByUpvotes = async (
    ascending: boolean,
    page: number = 0
  ) => {
    const uid = state.selectedPlayerUID;
    if (!uid) return;

    setIsLoading(true);
    setSelectedMethod("upvotes");
    setSortingMethod(ascending ? "asc" : "desc");

    try {
      const url = `/api/hexagen/by-upvotes-by-player/${uid}/${
        ascending ? "asc" : "desc"
      }/${page}`;
      const hexagons = await askAPI<Hexagen[]>(url, RequestMethod.GET);
      if (hexagons) {
        setHexagonsToShow(hexagons);
        setCurrentPage(page);
      }
    } catch (error) {
      console.error("Failed to fetch hexagons:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useAsyncEffect(async () => {
    await fetchPlayerInfo();
    await fetchHexagensByDate(false, 0);
  }, []);

  return (
    <div
      onMouseDown={closePopup}
      className={`appear-from-opacity-40-colored fixed shadow inset-0 flex justify-center items-start z-[10000]`}
      style={{
        perspective: "1000px",
        zIndex: zIndex,
      }}
    >
      <div
        className={`w-full max-w-[480px] max-h-[80vh] sm:ml-8 mt-[73px] relative bg-black text-white shadow m-2`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="text-center overflow-y-auto max-h-[80vh] py-4 px-6">
          <h2 className="text-2xl font-bold mt-6">
            {playerInfo?.name}
            <span className="text-sm text-gray-300 ml-2 font-normal">
              {Math.round(playerInfo?.rarityRating || 0)}
            </span>
          </h2>
          <div className="text-sm text-gray-300 mt-4 mb-3">
            {playerInfo?.points}⚡️ |{" "}{playerInfo?.sparks}🌟 |{" "}
            {playerInfo?.hexagensNumber}<span className="inline-block text-yellow-500" style={{ transform: "translateY(-1px)" }}>⬢</span>
          </div>

          <div className="text-sm mb-4 flex justify-center select-none">
            {(() => {
              const isDateSelected = selectedMethod !== "points" && selectedMethod !== "upvotes";
              const dateArrow = isDateSelected
                ? sortingMethod === "asc"
                  ? "▲"
                  : "▼"
                : "▼";
              const pointsArrow = selectedMethod === "points"
                ? sortingMethod === "asc"
                  ? "▲"
                  : "▼"
                : "▼";
              const upvotesArrow = selectedMethod === "upvotes"
                ? sortingMethod === "asc"
                  ? "▲"
                  : "▼"
                : "▼";
              return (
                <>
                  <span
                    className={`px-2 cursor-pointer ${
                      isDateSelected ? "font-bold" : ""
                    }`}
                    onClick={() => {
                      if (isDateSelected) {
                        const nextAsc = sortingMethod !== "asc";
                        setCurrentPage(0);
                        fetchHexagensByDate(nextAsc, 0);
                      } else {
                        setCurrentPage(0);
                        fetchHexagensByDate(false, 0);
                      }
                    }}
                  >
                    date {dateArrow}
                  </span>
                  |
                  <span
                    className={`pl-2 pr-2 cursor-pointer ${
                      selectedMethod === "points" ? "font-bold" : ""
                    }`}
                    onClick={() => {
                      if (selectedMethod === "points") {
                        const nextAsc = sortingMethod !== "asc";
                        setCurrentPage(0);
                        fetchHexagensByPoints(nextAsc, 0);
                      } else {
                        setCurrentPage(0);
                        fetchHexagensByPoints(false, 0);
                      }
                    }}
                  >
                    score {pointsArrow}
                  </span>
                  |
                  <span
                    className={`pl-2 cursor-pointer ${
                      selectedMethod === "upvotes" ? "font-bold" : ""
                    }`}
                    onClick={() => {
                      if (selectedMethod === "upvotes") {
                        const nextAsc = sortingMethod !== "asc";
                        setCurrentPage(0);
                        fetchHexagensByUpvotes(nextAsc, 0);
                      } else {
                        setCurrentPage(0);
                        fetchHexagensByUpvotes(false, 0);
                      }
                    }}
                  >
                    upvotes {upvotesArrow}
                  </span>
                </>
              );
            })()}
          </div>

          {isLoading ? (
            <div className="flex justify-center items-center h-32">
              <div className="text-gray-400">Loading...</div>
            </div>
          ) : (
            <CardTileList hexagens={hexagonsToShow} />
          )}

          {totalPages > 1 && (
            <>
              <div className="text-sm text-gray-400 text-center mt-4">
                Page {currentPage + 1} of {totalPages}
              </div>
              <div className="flex justify-center items-center gap-1 mt-2 mb-4">
                {getVisiblePages().map((page, index) => (
                  <span key={index}>
                    {page === "..." ? (
                      <span className="px-0 py-1 text-sm text-gray-500">
                        ...
                      </span>
                    ) : (
                      <button
                        onClick={() => goToPage(page as number)}
                        disabled={isLoading}
                        className={`px-2 py-1 text-sm ${
                          currentPage === page
                            ? "bg-white text-black font-bold"
                            : isLoading
                            ? "text-gray-500 cursor-not-allowed"
                            : "text-white hover:bg-gray-800 cursor-pointer"
                        }`}
                      >
                        {(page as number) + 1}
                      </button>
                    )}
                  </span>
                ))}
              </div>
            </>
          )}
        </div>
        <div
          onClick={closePopup}
          className="absolute right-[0px] top-[0px] select-none cursor-pointer text-[25px] text-white z-[1000000] bg-black px-3"
        >
          ✕
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-[30px] bg-gradient-to-t from-black to-transparent z-[10000000]"></div>
      </div>
    </div>
  );
};
